import { useCallback, useRef, useState } from 'react';
import {
	type OperationVariables,
	type MutationResult,
	type MutationFunctionOptions,
} from '@apollo/react-common';
import {
	type MutationHookOptions,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- `@confluence/apollo-relay-interop` package is the only one where such imports are allowed
	useMutation,
} from '@apollo/react-hooks';
import { type GraphQLTaggedNode, commitMutation, useRelayEnvironment } from 'react-relay';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { isRelayEnabled } from './isRelayEnabled.ts';
import { useMemoObj } from './useMemoObj.ts';
import { toDocumentNode } from './toDocumentNode.ts';

export type MutationFunctionInteropSupportedOptions<TData, TVariables> = Pick<
	MutationFunctionOptions<TData, TVariables>,
	'variables' | 'optimisticResponse' | 'update'
> & {
	// @deprecated - use mutation options instead
	refetchQueries?: never;
};

export type MutationFunctionInterop<TData, TVariables> = (
	options?: MutationFunctionInteropSupportedOptions<TData, TVariables>,
) => Promise<{ data?: TData }>;

export type UseMutationInteropSupportedResult<TData, TVariables> = [
	MutationFunctionInterop<TData, TVariables>,
	Pick<MutationResult<TData>, 'data' | 'error' | 'loading' | 'called'>,
];

/** Helper type to allow for easier overrides ion the `UseMutationInteropSupportedArgs` type */
type MutationUpdateParamType<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = Required<MutationHookOptions<TData, TVariables>>['update'];

export type UseMutationInteropSupportedArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> =
	| [GraphQLTaggedNode]
	| [
			GraphQLTaggedNode,
			Pick<
				MutationHookOptions<TData, TVariables>,
				| 'variables'
				| 'onCompleted'
				| 'onError'
				| 'errorPolicy'
				| 'optimisticResponse'
				| 'context'
				| 'fetchPolicy'
				| 'awaitRefetchQueries'
			> & {
				refetchQueries?:
					| Array<{ query: GraphQLTaggedNode; variables?: any }>
					| ((response: { data: TData }) => Array<{ query: GraphQLTaggedNode; variables?: any }>);
				update?(
					_: never,
					mutationResult: Parameters<MutationUpdateParamType<TData, TVariables>>[1],
				): ReturnType<MutationUpdateParamType<TData, TVariables>>;
				/**
				 * Ensures that the interop in Apollo build variant uses the AGG client
				 */
				interopApolloAggClient?: boolean;
				/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
				__callerPackage__?: string;
			},
	  ];

export function useMutationInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	...args: UseMutationInteropSupportedArgs<TData, TVariables>
): UseMutationInteropSupportedResult<TData, TVariables> {
	if (isRelayEnabled(args[1]?.__callerPackage__)) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useMutationInteropRelay<TData, TVariables>(...args);
	} else {
		const { interopApolloAggClient, ...apolloOptions } = args[1] ?? {};
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useMutation<TData, TVariables>(
			...[
				toDocumentNode(args[0]),
				{
					...apolloOptions,
					...(interopApolloAggClient ? { client: window['GLOBAL_APOLLO_AGG_CLIENT'] } : {}),
					refetchQueries: createRefetchQueriesFn(),
					update(_: any, result: Parameters<MutationUpdateParamType<TData, TVariables>>[1]) {
						return apolloOptions?.update?.(null as never, result);
					},
				},
			],
		);
	}

	function createRefetchQueriesFn() {
		if (typeof args[1]?.refetchQueries === 'undefined') return undefined;

		return (response: { data: TData }) => {
			const graphqlTaggedNodeArray =
				typeof args[1]?.refetchQueries === 'function'
					? args[1]?.refetchQueries(response)
					: (args[1]?.refetchQueries ?? []);

			return graphqlTaggedNodeArray?.map((value) => {
				if (typeof value === 'string') {
					// handling the case where simply the query name is specified, e.g. `refetchQueries: ['SomeQuery']`
					return value;
				}

				return {
					query: toDocumentNode(value.query),
					variables: value.variables,
				};
			});
		};
	}
}

function useMutationInteropRelay<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	...[mutation, options]: UseMutationInteropSupportedArgs<TData, TVariables>
): UseMutationInteropSupportedResult<TData, TVariables> {
	// TODO: shim accepted but not yet supported parameters (i.e. those params WILL be supported later)
	const unsupportedParams = ['awaitRefetchQueries', 'refetchQueries'];
	const unsupportedParam = unsupportedParams.find((param) => options && param in options);
	if (unsupportedParam) {
		throw new Error(`[Relay migration] ${unsupportedParam} not supported yet (but should be)`);
	}

	const environment = useRelayEnvironment();
	// Extract variables and memoize them to prevent unnecessary re-renders
	const variables = useMemoObj<TVariables>(options?.variables, options?.__callerPackage__);

	// Extract context and memoize
	const context = useMemoObj(options?.context, options?.__callerPackage__);

	// State to manage the mutation result
	const [data, setData] = useState<TData | undefined>(undefined);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<any>(undefined);
	const [called, setCalled] = useState(false);

	const onCompletedRef = useRef(options?.onCompleted);
	onCompletedRef.current = options?.onCompleted;

	const onErrorRef = useRef(options?.onError);
	onErrorRef.current = options?.onError;

	const defaultUpdateRef = useRef(options?.update);
	defaultUpdateRef.current = options?.update;

	const defaultOptimisticResponseRef = useRef(options?.optimisticResponse);
	defaultOptimisticResponseRef.current = options?.optimisticResponse;

	// Create the mutation function
	const mutate = useCallback(
		(mutationOptions?: MutationFunctionInteropSupportedOptions<TData, TVariables>) => {
			setCalled(true);
			setLoading(true);
			setError(undefined);

			const operationVariables = mutationOptions?.variables || variables || {};

			// Resolve update: call-time takes priority over hook-level default
			const update = mutationOptions?.update ?? defaultUpdateRef.current;

			// Resolve optimisticResponse: call-time takes priority over hook-level default
			const optimisticResponse =
				mutationOptions?.optimisticResponse ?? defaultOptimisticResponseRef.current;

			// Resolve optimistic data for the update callback
			let resolvedOptimisticResponse: TData | undefined;
			try {
				if (optimisticResponse) {
					resolvedOptimisticResponse =
						typeof optimisticResponse === 'function'
							? (optimisticResponse as (vars: TVariables) => TData)(
									operationVariables as TVariables,
								)
							: (optimisticResponse as TData);

					// Call update immediately with optimistic data (matching Apollo behavior)
					update?.(null as never, { data: resolvedOptimisticResponse });
				}
			} catch (err) {
				try {
					onErrorRef.current?.(err as ApolloErrorInterop);
				} catch {
					// Ignore onError callback failure
				}
				setError(err);
				setLoading(false);
				return Promise.reject(err);
			}

			return new Promise<{ data?: TData }>((resolve, reject) => {
				commitMutation(environment, {
					mutation,
					cacheConfig: { metadata: context },
					variables: operationVariables,
					optimisticResponse: resolvedOptimisticResponse as any,
					onCompleted: (response: any, errors) => {
						try {
							if (errors && errors.length > 0) {
								const error = new Error(errors[0].message);

								// Rollback optimistic update on error
								try {
									if (optimisticResponse && update) {
										update(null as never, { data: undefined });
									}
								} catch {
									// Ignore rollback failure — still proceed to reject with the original error
								}

								try {
									onErrorRef.current?.(error as ApolloErrorInterop);
								} catch {
									// Ignore onError callback failure
								}
								setError(error);
								setLoading(false);
								reject(error);
							} else {
								update?.(null as never, { data: response as TData });

								setData(response as TData);
								setLoading(false);

								// Call onCompleted before resolve so any throw is caught and rejects the promise
								onCompletedRef.current?.(response);
								resolve({ data: response as TData });
							}
						} catch (err) {
							try {
								onErrorRef.current?.(err as ApolloErrorInterop);
							} catch {
								// Ignore onError callback failure
							}
							setData(undefined);
							setError(err);
							setLoading(false);
							reject(err);
						}
					},
					onError: (error: any) => {
						try {
							// Rollback optimistic update on error
							if (optimisticResponse && update) {
								update(null as never, { data: undefined });
							}
						} catch {
							// Ignore rollback failure — still proceed to reject with the original error
						}

						try {
							onErrorRef.current?.(error);
						} catch {
							// Ignore onError callback failure — still proceed to reject with the original error
						}
						setLoading(false);
						setError(error);
						reject(error);
					},
				});
			});
		},
		[environment, mutation, variables, context],
	);

	return [
		mutate,
		{
			data,
			loading,
			error,
			called,
		},
	];
}
